import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { useMarkNichtGeeignet } from '../../api/bewerber';
import { NICHT_GEEIGNET_REASONS, type NichtGeeignetDto, type Bewerber } from '../../types/bewerber';

interface NichtGeeignetModalProps {
  isOpen: boolean;
  onClose: () => void;
  bewerber: Bewerber;
  onSuccess?: () => void;
}

interface FormValues {
  selectedReasons: string[];
  freitextGrund: string;
}

export function NichtGeeignetModal({
  isOpen,
  onClose,
  bewerber,
  onSuccess,
}: NichtGeeignetModalProps) {
  const markNichtGeeignetMutation = useMarkNichtGeeignet();
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    watch,
  } = useForm<FormValues>({
    defaultValues: {
      selectedReasons: [],
      freitextGrund: '',
    },
  });

  const selectedReasons = watch('selectedReasons');
  const freitextGrund = watch('freitextGrund');

  // Validation: at least one checkbox OR text required
  const isValid = selectedReasons.length > 0 || freitextGrund.trim().length > 0;

  const handleFormSubmit = async (data: FormValues) => {
    if (!isValid) {
      setError('Bitte wählen Sie mindestens einen Grund aus oder geben Sie einen Freitext ein.');
      return;
    }

    setError(null);
    try {
      const dto: NichtGeeignetDto = {
        selectedReasons: data.selectedReasons.length > 0 ? data.selectedReasons : undefined,
        freitextGrund: data.freitextGrund.trim() || undefined,
      };

      await markNichtGeeignetMutation.mutateAsync({
        id: bewerber.id,
        data: dto,
      });

      toast.success('Bewerber als nicht geeignet markiert');
      reset();
      onClose();
      onSuccess?.();
    } catch (err: any) {
      let errorMessage = 'Fehler beim Markieren als nicht geeignet';

      const responseMessage = err?.response?.data?.message;
      if (Array.isArray(responseMessage)) {
        errorMessage = responseMessage.join(', ');
      } else if (typeof responseMessage === 'string') {
        errorMessage = responseMessage;
      } else if (err?.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
      toast.error(errorMessage);
      console.error('Failed to mark bewerber as nicht geeignet:', err);
    }
  };

  const handleClose = () => {
    reset();
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  const bewerberName = `${bewerber.firstName} ${bewerber.lastName}`;

  return createPortal(
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={handleClose}
        />

        {/* Modal */}
        <div className="relative inline-block bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:max-w-lg sm:w-full">
          <form onSubmit={handleSubmit(handleFormSubmit)}>
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-amber-100 sm:mx-0 sm:h-10 sm:w-10">
                  <svg className="h-6 w-6 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left flex-1">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Als nicht geeignet markieren
                  </h3>
                  <p className="mt-2 text-sm text-gray-500">
                    <span className="font-medium">{bewerberName}</span> wird auf den Status "Nicht geeignet" gesetzt.
                  </p>

                  {error && (
                    <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded-md">
                      <p className="text-sm text-red-600">{error}</p>
                    </div>
                  )}

                  <div className="mt-4 space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Grund (mindestens eine Auswahl oder Freitext erforderlich)
                      </label>
                      <div className="space-y-2">
                        {NICHT_GEEIGNET_REASONS.map((reason) => (
                          <label key={reason} className="flex items-center">
                            <input
                              type="checkbox"
                              value={reason}
                              {...register('selectedReasons')}
                              className="h-4 w-4 text-amber-600 focus:ring-amber-500 border-gray-300 rounded"
                            />
                            <span className="ml-3 text-sm text-gray-900">{reason}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label htmlFor="freitextGrund" className="block text-sm font-medium text-gray-700">
                        Freitext
                      </label>
                      <textarea
                        id="freitextGrund"
                        rows={3}
                        {...register('freitextGrund')}
                        placeholder="Zusätzliche Begründung..."
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-amber-500 focus:ring-amber-500 sm:text-sm"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                type="submit"
                disabled={markNichtGeeignetMutation.isPending || !isValid}
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-amber-600 text-base font-medium text-white hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
              >
                {markNichtGeeignetMutation.isPending ? 'Wird markiert...' : 'Als nicht geeignet markieren'}
              </button>
              <button
                type="button"
                onClick={handleClose}
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              >
                Abbrechen
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>,
    document.body
  );
}
